import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from 'shared/lib/cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogTitle = DialogPrimitive.Title;
export const DialogDescription = DialogPrimitive.Description;

export type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  title: string;
  description?: string;
  /** Hide the built-in close button when the body provides its own. */
  hideClose?: boolean;
};

/** Radix handles the scrim, focus trap, Escape and focus return; this only skins them. */
export const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  ({ className, title, description, hideClose, children, ...props }, ref) => (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ground/80 backdrop-blur-sm" />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-2)] border border-hairline/20 bg-surface-overlay p-5 text-ink shadow-2xl focus:outline-none',
          className,
        )}
        // W3: opt out of Radix's describedby wiring ONLY when this content has
        // no description to point at. A supplied `description` renders a real
        // DialogPrimitive.Description below and must keep its aria-describedby,
        // so this is conditional where Viewer's is unconditional. It has to be
        // a conditional SPREAD, not `aria-describedby={cond ? undefined : …}`:
        // Radix emits its own value first and then spreads contentProps over
        // it, and an explicit `undefined` in a spread overwrites — so a key
        // that is always present would suppress the described case too.
        {...(description ? {} : { 'aria-describedby': undefined })}
        {...props}
      >
        <DialogPrimitive.Title className="font-display text-base font-semibold">{title}</DialogPrimitive.Title>
        {description && <DialogPrimitive.Description className="mt-1 font-body text-xs text-ink-muted">{description}</DialogPrimitive.Description>}
        <div className="mt-4">{children}</div>
        {!hideClose && (
          <DialogPrimitive.Close aria-label="Close" className="absolute right-3 top-3 rounded-[var(--radius-2)] p-1 text-ink-muted hover:bg-surface-high hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60">
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  ),
);
DialogContent.displayName = 'DialogContent';
